import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";

/**
 * MindfulMe (love funnel) subscriptions of the signed-in user.
 * The API resolves them in Stripe by the session email — the client never sends an email.
 */
export type UserSubscription = {
  id: string;
  /** "main" — love plan, "coach" — coaching upsell */
  kind: "main" | "coach";
  planName: string;
  /** price per period in major units, e.g. 39.99 */
  amount: number;
  currency: string;
  /** billing period, e.g. 28 + "day" or 1 + "month" */
  intervalCount: number;
  interval: "day" | "week" | "month" | "year";
  status: "active" | "trialing" | "past_due" | "canceled" | string;
  cancelAtPeriodEnd: boolean;
  /** ISO date: next billing date, or the access end date when cancelAtPeriodEnd */
  currentPeriodEnd: string;
};

export const CANCEL_REASONS = [
  "too_expensive",
  "not_using",
  "got_what_needed",
  "content_not_right",
  "technical_problems",
  "something_else",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

const KEY = ["me", "subscriptions"] as const;

export function useSubscriptions() {
  return useQuery<UserSubscription[], ApiError>({
    queryKey: KEY,
    queryFn: () => api<UserSubscription[]>("/api/me/subscriptions"),
    staleTime: 30_000,
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; reason: CancelReason; message?: string }) =>
      api<UserSubscription>(`/api/me/subscriptions/${encodeURIComponent(p.id)}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason: p.reason, message: p.message ?? "" }),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<UserSubscription[]>(KEY, (prev) =>
        prev?.map((s) => (s.id === updated.id ? updated : s)),
      );
    },
  });
}

export function formatMoney(amount: number, currency: string, language: string): string {
  const locale = language === "es" ? "es-ES" : language === "pt" ? "pt-BR" : "en-US";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
}

export function formatDate(iso: string, language: string): string {
  const locale = language === "es" ? "es-ES" : language === "pt" ? "pt-BR" : "en-US";
  return new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}
